/**
 * StaffPromotionsWorkspace
 *
 * Manages patient-visible promotions via the Segments API.
 * Orchestrates: list, create, edit, delete, segment targeting.
 *
 * Responsibilities (SRP):
 *   - data fetching & state management only
 *   - delegates rendering to PromotionsList / PromotionFormModal
 */
import { useCallback, useEffect, useState } from 'react'
import type { StaffPromotion, StaffSegment, StaffPromotionInput } from '@unified-lk/api-contracts'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import type { AuthSession } from '../../../types/plans'
import { staffApi } from '../../../api/staff'
import { PromotionsList } from './PromotionsList'
import { PromotionFormModal } from './PromotionFormModal'

type ModalState =
  | { open: false }
  | { open: true; promotion: StaffPromotion | null }

interface Props {
  bootstrap: TenantBootstrapModel
  session: AuthSession
}

export function StaffPromotionsWorkspace({ bootstrap, session }: Props) {
  // ── Data ───────────────────────────────────────────────────────────────────
  const [promotions, setPromotions] = useState<StaffPromotion[]>([])
  const [segments, setSegments] = useState<StaffSegment[]>([])
  const [loading, setLoading] = useState(true)
  const [segmentsLoading, setSegmentsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [segmentsError, setSegmentsError] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<number | null>(null)
  const [toast, setToast] = useState<{ type: 'ok' | 'err'; msg: string } | null>(null)

  // ── Modal ──────────────────────────────────────────────────────────────────
  const [modal, setModal] = useState<ModalState>({ open: false })

  // ── Load ───────────────────────────────────────────────────────────────────
  const loadPromotions = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await staffApi.getPromotions(bootstrap, session)
      setPromotions(res.promotions)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить акции')
    } finally {
      setLoading(false)
    }
  }, [bootstrap, session])

  const loadSegments = useCallback(async () => {
    setSegmentsLoading(true)
    setSegmentsError(null)
    try {
      const res = await staffApi.getPromotionSegments(bootstrap, session)
      setSegments(res.segments)
    } catch (e) {
      setSegmentsError(e instanceof Error ? e.message : 'Не удалось загрузить сегменты')
    } finally {
      setSegmentsLoading(false)
    }
  }, [bootstrap, session])

  useEffect(() => {
    void loadPromotions()
    void loadSegments()
  }, [loadPromotions, loadSegments])

  // ── Auto-dismiss toast ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(null), 3500)
    return () => clearTimeout(t)
  }, [toast])

  // ── Actions ────────────────────────────────────────────────────────────────
  async function handleSave(input: StaffPromotionInput) {
    const editing = modal.open && modal.promotion !== null ? modal.promotion : null

    if (editing) {
      await staffApi.updatePromotion(bootstrap, session, editing.id, input)
      setToast({ type: 'ok', msg: 'Акция обновлена' })
    } else {
      await staffApi.createPromotion(bootstrap, session, input)
      setToast({ type: 'ok', msg: 'Акция создана' })
    }

    await loadPromotions()
  }

  async function handleDelete(id: number) {
    if (!window.confirm('Удалить акцию? Это действие необратимо.')) return
    setDeleting(id)
    try {
      await staffApi.deletePromotion(bootstrap, session, id)
      setToast({ type: 'ok', msg: 'Акция удалена' })
      await loadPromotions()
    } catch (e) {
      setToast({ type: 'err', msg: e instanceof Error ? e.message : 'Ошибка удаления' })
    } finally {
      setDeleting(null)
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <section className="grid gap-4">

      {/* Header card */}
      <div className="flex flex-col gap-4 rounded-[28px] border border-slate-200/70 bg-white p-6 shadow-[0_16px_36px_rgba(15,23,42,0.06)] sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="mb-1 text-xs font-bold uppercase tracking-[0.14em] text-sky-600">Управление акциями</p>
          <h2 className="text-xl font-bold text-slate-900">Акции для пациентов</h2>
          <p className="mt-1 text-sm text-slate-500">
            Создайте персонализированные предложения и привяжите их к сегментам пациентов.
            Акции отображаются в ЛК пациента на главной и в разделе «Промо».
          </p>
        </div>
        <button
          type="button"
          onClick={() => setModal({ open: true, promotion: null })}
          className="shrink-0 rounded-2xl bg-[linear-gradient(135deg,#0099ff_0%,#0077cc_100%)] px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:opacity-90 transition"
        >
          <i className="bi bi-plus-lg mr-1.5" />
          Создать акцию
        </button>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`rounded-2xl px-5 py-3 text-sm font-medium shadow-sm ${
          toast.type === 'ok'
            ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {toast.type === 'ok' ? '✅' : '❌'} {toast.msg}
        </div>
      )}

      {/* Main table card */}
      <div className="rounded-[28px] border border-slate-200/70 bg-white shadow-[0_16px_36px_rgba(15,23,42,0.06)] overflow-hidden">

        {/* Table header meta */}
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div className="flex items-center gap-2">
            <i className="bi bi-megaphone-fill text-sky-500" />
            <span className="text-sm font-semibold text-slate-700">Список акций</span>
            {!loading && (
              <span className="rounded-full bg-sky-50 px-2.5 py-0.5 text-[11px] font-bold text-sky-600">
                {promotions.length}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => void loadPromotions()}
            disabled={loading}
            className="rounded-xl p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50 transition"
            title="Обновить"
          >
            <i className={`bi bi-arrow-clockwise text-base ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Body */}
        <div className="px-2 py-2">
          {loading ? (
            <div className="flex flex-col items-center py-16 text-slate-400">
              <span className="mb-3 inline-block h-8 w-8 animate-spin rounded-full border-2 border-sky-400 border-t-transparent" />
              Загрузка акций...
            </div>
          ) : error ? (
            <div className="flex flex-col items-center py-12">
              <p className="mb-3 text-sm text-slate-500">{error}</p>
              <button
                type="button"
                onClick={() => void loadPromotions()}
                className="rounded-2xl border border-sky-200 bg-sky-50 px-4 py-2 text-sm font-medium text-sky-700 hover:bg-sky-100 transition"
              >
                Повторить
              </button>
            </div>
          ) : (
            <PromotionsList
              promotions={promotions}
              segments={segments}
              onEdit={(p) => setModal({ open: true, promotion: p })}
              onDelete={(id) => void handleDelete(id)}
              deleting={deleting}
            />
          )}
        </div>
      </div>

      {/* Info card */}
      <div className="rounded-[28px] border border-slate-200/70 bg-slate-50 p-5 text-sm text-slate-600">
        <p className="mb-2 font-semibold text-slate-700">Как это работает</p>
        <ul className="grid gap-1.5 list-none">
          <li className="flex items-start gap-2">
            <i className="bi bi-1-circle-fill mt-0.5 shrink-0 text-sky-500" />
            Создайте акцию с заголовком, описанием и изображением.
          </li>
          <li className="flex items-start gap-2">
            <i className="bi bi-2-circle-fill mt-0.5 shrink-0 text-sky-500" />
            Привяжите к одному или нескольким сегментам пациентов (максимум 3).
          </li>
          <li className="flex items-start gap-2">
            <i className="bi bi-3-circle-fill mt-0.5 shrink-0 text-sky-500" />
            Акция автоматически появится в ЛК пациентов из выбранных сегментов — на главной странице и в разделе «Промо и акции».
          </li>
        </ul>
      </div>

      {/* Modal */}
      {modal.open && (
        <PromotionFormModal
          promotion={modal.promotion}
          segments={segments}
          segmentsLoading={segmentsLoading}
          segmentsError={segmentsError}
          onSave={handleSave}
          onClose={() => setModal({ open: false })}
        />
      )}
    </section>
  )
}
